import { TrashIcon } from "@heroicons/react/24/outline";
import { useFetcher } from "@remix-run/react";
import { type Dispatch, type SetStateAction, useEffect } from "react";
import type { CartAction, CartFlatten } from "~/types";
import { isBlackFridayPromoEnabled } from "~/utils/dates";
// import selectBG from "../../images/Select.svg";
import selectBGLM from "../../images/SelectLight.svg";
import loadersvg from "../../images/loader.svg";
import Alert from "../alert.component";
import Neum from "../neum.component";
export default function ItemsList({
  cart,
  setCartState,
}: {
  cart: CartFlatten;
  setCartState: Dispatch<SetStateAction<CartFlatten | undefined>>;
}) {
  const cartLines = cart.lines;
  const cartId = cart.id;
  const cartFetcher = useFetcher<CartAction>();

  //TODO: change the default 10 items in the select for a more realistic way
  const options: { value: number; label: string }[] = [];

  for (let i = 1; i <= 10; i++) {
    options.push({ value: i, label: `${i}` });
  }

  const handleQuantityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const form = event.target.form;

    if (form) {
      const formData = new FormData(form);
      const cartId = formData.get("cartId") as string;
      const lineId = formData.get("lineId") as string;
      const quantity = formData.get("quantity") as string;

      cartFetcher.submit({ cartId, lineId, quantity }, { method: "post", action: "/cart/update-quantity" });
    }
  };

  useEffect(() => {
    if (cartFetcher.state === "idle" && cartFetcher.data) {
      setCartState(cartFetcher.data.cart as CartFlatten);
      let totalQuantity = 0;

      if (cartFetcher.data.cart) {
        totalQuantity = cartFetcher.data.cart.totalQuantity;
      }
      const event = new CustomEvent<number>("cartUpdated", {
        detail: totalQuantity,
        bubbles: true,
      });

      window.dispatchEvent(event);
    }
  });

  const showDiscount = isBlackFridayPromoEnabled();

  return (
    <section aria-labelledby="cart-items" className="lg:col-span-8">
      {cartFetcher.state === "idle" && cartFetcher.data && cartFetcher.data.error ? (
        <Alert message={cartFetcher.data.error} typeAlert="Error" />
      ) : null}

      <h2 id="cart-items" className="sr-only">
        Items in your shopping cart
      </h2>

      <ul>
        {cartLines.map((product, productIdx) => (
          <li key={product.id} className="flex pb-1.5 pt-3 md:py-3 md:pb-5 md:pr-5">
            <Neum className="w-full">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4 md:col-span-6 md:pr-2">
                  <img
                    src={product.merchandise.image?.url as string}
                    alt={product.merchandise.image?.altText ?? ""}
                    className="h-28 w-full rounded-l-lg object-cover pb-px pt-px sm:h-48 md:h-72"
                  />
                </div>
                <div className="relative col-span-8 md:col-span-6">
                  <div className="absolute inline-block h-full w-full content-between">
                    <div className="flex justify-between pr-2 pt-2">
                      <h3 className="mt-0 text-xs md:mt-2 md:text-xl">
                        <a
                          href={`/products/${product.merchandise.product.handle}`}
                          className="font-semibold line-clamp-2 hover:text-admirerBlue dark:text-white md:font-bold"
                        >
                          {product.merchandise.product.title}
                        </a>
                      </h3>
                    </div>
                    <div className="p mt-mx flex md:mt-4">
                      <p className="text-sm font-light text-admirerBlack-700 line-clamp-1 md:text-2xl">
                        {product.merchandise.title === "Default Title" ? "" : product.merchandise.title}
                      </p>
                    </div>
                    {/* TODO: we should not use absolute almost never */}
                    <div className="absolute bottom-0 mb-1.5 grid w-full grid-cols-5  gap-0 md:absolute md:mb-2.5 md:grid-cols-6 md:gap-1">
                      <div className={showDiscount ? "col-span-6 md:col-span-3 lg:-mt-5" : "col-span-6 md:col-span-3"}>
                        <p className="mb-0.5 text-sm font-medium uppercase dark:text-white sm:text-base md:text-2xl">
                          <span id="value" className={showDiscount ? "line-through lg:block" : ""}>
                            ${product.merchandise.priceV2.amount}{" "}
                          </span>
                          {showDiscount && (
                            <span id="discount-value" className="text-red-600">
                              {`$${Math.round(product.merchandise.priceV2.amount * 0.85 * 100) / 100} `}
                            </span>
                          )}
                          <span className="pl-1  text-xs uppercase">{product.merchandise.priceV2.currencyCode}</span>
                        </p>
                      </div>

                      <div className="col-span-6 mr-2.5 md:col-span-3 md:mr-3.5">
                        <cartFetcher.Form action="/cart/update-quantity" method="post">
                          <input type="hidden" name="cartId" defaultValue={cartId} />
                          <input type="hidden" name="lineId" value={product.id} />
                          <select
                            id={`quantity-${productIdx}`}
                            name="quantity"
                            className={`text-white-700 group-hover:text-white-800 md:text-md float-left mt-[3px] flex h-6 w-16 items-center rounded-lg bg-transparent py-0 text-center text-xs font-light focus:ring-0  md:float-right md:-mt-2 md:mb-px  md:h-12 md:w-24 md:text-lg lg:w-24 lg:pr-14 
                            ${cartFetcher.state !== "idle" ? "pointer-events-none" : ""}`}
                            style={{ backgroundImage: `url(${selectBGLM}); background-position-x: 73%` }}
                            onChange={handleQuantityChange}
                            defaultValue={product.quantity}
                          >
                            {options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </cartFetcher.Form>

                        <cartFetcher.Form action="/cart/delete" method="post">
                          <input type="hidden" name="cartId" defaultValue={cartId} />
                          <input type="hidden" name="lineId" value={product.id} />
                          {cartFetcher.state !== "idle" ? (
                            <div className="float-right md:mr-2 lg:mr-5">
                              <img
                                className="-mr-0.5 -mt-[5px] h-8 w-8 md:-mt-2  md:mr-0 md:h-12 md:w-12 lg:-mt-2 lg:h-12 lg:w-12 "
                                src={loadersvg}
                                alt="Loading"
                              />
                            </div>
                          ) : (
                            <button type="submit" className="float-right md:mr-2 lg:mr-5">
                              <TrashIcon
                                className="md:-mt-2px mt-1 h-5 w-auto group-hover:text-red-500 md:-mt-px md:h-8 lg:mt-px lg:h-8"
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </cartFetcher.Form>

                        <label htmlFor={`quantity-${productIdx}`} className="sr-only">
                          Quantity, {product.merchandise.title}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Neum>
          </li>
        ))}
      </ul>
    </section>
  );
}
